'use client';

import { motion } from 'framer-motion';
import { ReactNode } from 'react';

type FadeInDirection = 'up' | 'down' | 'left' | 'right';

interface FadeInProps {
  children: ReactNode;
  className?: string;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Animation duration (in seconds) */
  duration?: number;
  /** Direction from which the element appears */
  from?: FadeInDirection;
  /** Offset distance for slide effect (in pixels) */
  offset?: number;
}

function getInitialPosition(from: FadeInDirection, offset: number) {
  switch (from) {
    case 'up':
      return { x: 0, y: -offset }; // starts above, slides down
    case 'down':
      return { x: 0, y: offset }; // starts below, slides up
    case 'left':
      return { x: -offset, y: 0 }; // starts left, slides right
    case 'right':
      return { x: offset, y: 0 }; // starts right, slides left
  }
}

export function FadeIn({
  children,
  className,
  delay = 0,
  duration = 0.25,
  from = 'up',
  offset = 16,
}: FadeInProps) {
  const initialPosition = getInitialPosition(from, offset);

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, ...initialPosition }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1], // cubic-bezier for smooth easing
      }}
    >
      {children}
    </motion.div>
  );
}

interface FadeInGroupProps {
  children: ReactNode;
  className?: string;
  /** Delay between each child animation (in seconds) */
  staggerDelay?: number;
  /** Base delay before first child starts (in seconds) */
  baseDelay?: number;
  /** Animation duration for each child (in seconds) */
  duration?: number;
  /** Vertical offset for slide effect (in pixels) */
  yOffset?: number;
}

export function FadeInGroup({
  children,
  className,
  staggerDelay = 0.1,
  baseDelay = 0,
  duration: _duration = 0.4,
  yOffset: _yOffset = 16,
}: FadeInGroupProps) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: baseDelay,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

interface FadeInItemProps {
  children: ReactNode;
  className?: string;
  /** Animation duration (in seconds) */
  duration?: number;
  /** Vertical offset for slide effect (in pixels) */
  yOffset?: number;
}

export function FadeInItem({ children, className, duration = 0.4, yOffset = 16 }: FadeInItemProps) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: yOffset },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration,
            ease: [0.25, 0.1, 0.25, 1],
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}
